import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import apiClient from '../api/apiClient';

const SETTINGS_TABS = ['Credentials', 'Team Assignments', 'KPI Targets', 'Webhooks'];

// Matches server's SECRET_KEYS (routes/settings.js) - these come back from GET as a masked
// preview ("abcd****wxyz"), never the real value.
const SECRET_KEYS = new Set(['GHL_API_KEY', 'ST_CLIENT_SECRET', 'ST_APP_KEY']);

const GHL_FIELDS = [
  { key: 'GHL_API_KEY', label: 'GHL API Key', type: 'password', placeholder: 'ey...' },
  { key: 'GHL_LOCATION_ID', label: 'Location ID', type: 'text', placeholder: 'abc123...' },
];

const ST_FIELDS = [
  { key: 'ST_CLIENT_ID',   label: 'Client ID',    type: 'text',     placeholder: '' },
  { key: 'ST_CLIENT_SECRET', label: 'Client Secret', type: 'password', placeholder: '' },
  { key: 'ST_APP_KEY',    label: 'App Key',       type: 'password', placeholder: '' },
  { key: 'ST_TENANT_ID',  label: 'Tenant ID',     type: 'text',     placeholder: '' },
  { key: 'ST_BASE_URL',   label: 'Base URL',      type: 'text',     placeholder: 'https://api.servicetitan.io' },
  { key: 'ST_AUTH_URL',   label: 'Auth URL',      type: 'text',     placeholder: 'https://auth.servicetitan.io' },
];

function CredentialGroup({ title, fields, creds, hasExisting, onChange, onTest, testing, testResult }) {
  return (
    <div style={styles.group}>
      <div style={styles.groupHeader}>
        <h3 style={styles.groupTitle}>{title}</h3>
        <button onClick={onTest} disabled={testing} style={styles.testBtn}>
          {testing ? 'Testing…' : 'Test Connection'}
        </button>
      </div>
      {testResult && (
        <div style={{ ...styles.testResult, background: testResult.ok ? '#f0fdf4' : '#fef2f2', color: testResult.ok ? '#166534' : '#dc2626', border: `1px solid ${testResult.ok ? '#bbf7d0' : '#fecaca'}` }}>
          <div>{testResult.ok ? '✅ ' : '❌ '}{testResult.message}</div>
          {testResult.detail && <div style={{ fontSize: '0.72rem', opacity: 0.85, marginTop: '0.25rem', wordBreak: 'break-word' }}>{testResult.detail}</div>}
        </div>
      )}
      <div style={styles.fieldGrid}>
        {fields.map(f => {
          const isSecret = SECRET_KEYS.has(f.key);
          return (
            <div key={f.key} style={styles.field}>
              <label style={styles.label}>{f.label}</label>
              <input
                type={f.type}
                value={creds[f.key] || ''}
                onChange={e => onChange(f.key, e.target.value)}
                placeholder={isSecret && hasExisting[f.key] ? 'Already set — type to replace' : (creds[f.key] ? '' : f.placeholder || 'Not set')}
                style={styles.input}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Team Assignments Tab ─────────────────────────────────────────────────────
function TeamAssignmentsTab() {
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selManager, setSelManager] = useState('');
  const [selEmployee, setSelEmployee] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, userRes, asnRes] = await Promise.all([
        apiClient.get('/teams/employees'),
        apiClient.get('/users'),
        apiClient.get('/teams/assignments'),
      ]);
      setEmployees(empRes.data.employees || []);
      setManagers((userRes.data.users || []).filter(u => u.role === 'manager' && u.active));
      setAssignments(asnRes.data.assignments || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = async () => {
    if (!selManager || !selEmployee) return;
    setSaving(true);
    try {
      await apiClient.post('/teams/assignments', { employee_id: Number(selEmployee), manager_id: Number(selManager) });
      setSelEmployee('');
      await load();
    } catch (e) {
      // ignore duplicate
    } finally { setSaving(false); }
  };

  const unassign = async (employeeId, managerId) => {
    await apiClient.delete(`/teams/assignments/${employeeId}/${managerId}`);
    await load();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={styles.group}>
        <h3 style={styles.groupTitle}>Assign Employee to Manager</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={asgStyles.label}>Manager</label>
            <select value={selManager} onChange={e => setSelManager(e.target.value)} style={asgStyles.select}>
              <option value="">— Select Manager —</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
            </select>
          </div>
          <div>
            <label style={asgStyles.label}>Employee</label>
            <select value={selEmployee} onChange={e => setSelEmployee(e.target.value)} style={asgStyles.select}>
              <option value="">— Select Employee —</option>
              {employees.filter(e => e.active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <button onClick={assign} disabled={!selManager || !selEmployee || saving} style={asgStyles.btn}>
            {saving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>

      {managers.map(mgr => {
        const mgrAssignments = assignments.filter(a => a.manager_id === mgr.id);
        return (
          <div key={mgr.id} style={styles.group}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ ...styles.groupTitle, margin: 0 }}>{mgr.name || mgr.email}</h3>
              <span style={{ fontSize: '0.8rem', color: '#64748b', background: '#f1f5f9', padding: '0.2rem 0.625rem', borderRadius: '999px' }}>{mgrAssignments.length} employees</span>
            </div>
            {mgrAssignments.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>No employees assigned</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {mgrAssignments.map(a => (
                  <div key={a.employee_id} style={asgStyles.chip}>
                    <span>{a.employee_name}</span>
                    <button onClick={() => unassign(a.employee_id, mgr.id)} style={asgStyles.chipRemove} title="Remove">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {managers.length === 0 && (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No managers found. Create manager-role users first.</p>
      )}
    </div>
  );
}

const asgStyles = {
  label: { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' },
  select: { padding: '0.5rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', minWidth: '180px' },
  btn: { padding: '0.5rem 1.125rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', alignSelf: 'flex-end' },
  chip: { display: 'flex', alignItems: 'center', gap: '0.375rem', background: '#ede9fe', color: '#6366f1', padding: '0.3rem 0.625rem 0.3rem 0.875rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600' },
  chipRemove: { background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: '1rem', lineHeight: 1, padding: 0, fontWeight: '700' },
};

// ── KPI Targets Tab ──────────────────────────────────────────────────────────
function KpiTargetsTab() {
  const [departments, setDepartments] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTargets, setEditTargets] = useState({});
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, kRes] = await Promise.all([
        apiClient.get('/forecast/departments'),
        apiClient.get('/forecast/kpis'),
      ]);
      setDepartments(dRes.data.departments || []);
      setKpis(kRes.data.kpis || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveTarget = async (kpiId) => {
    const val = editTargets[kpiId];
    if (val === undefined) return;
    setSaving(s => ({...s, [kpiId]: true}));
    try {
      await apiClient.put(`/forecast/kpis/${kpiId}`, { target: Number(val) });
      await load();
      setEditTargets(t => { const n = {...t}; delete n[kpiId]; return n; });
    } catch (e) {
      // ignore
    } finally { setSaving(s => ({...s, [kpiId]: false})); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {departments.filter(d => d.active).map(dept => {
        const deptKpis = kpis.filter(k => k.dept_id === dept.id && k.active);
        return (
          <div key={dept.id} style={styles.group}>
            <h3 style={styles.groupTitle}>{dept.name}</h3>
            {deptKpis.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>No KPIs — add them in the Forecast page.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {deptKpis.map(kpi => {
                  const isEditing = kpiId => editTargets[kpiId] !== undefined;
                  const val = isEditing(kpi.id) ? editTargets[kpi.id] : kpi.target;
                  return (
                    <div key={kpi.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '160px' }}>
                        <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>{kpi.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Unit: {kpi.unit}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>Monthly Target:</label>
                        <input
                          type="number"
                          value={val ?? ''}
                          onChange={e => setEditTargets(t => ({...t, [kpi.id]: e.target.value}))}
                          style={{ ...styles.input, width: '100px', padding: '0.375rem 0.625rem' }}
                        />
                        {editTargets[kpi.id] !== undefined && (
                          <button onClick={() => saveTarget(kpi.id)} disabled={saving[kpi.id]} style={{ padding: '0.375rem 0.75rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>
                            {saving[kpi.id] ? '…' : 'Save'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {departments.filter(d => d.active).length === 0 && (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No departments found. Add them in the Forecast → Department KPIs tab.</p>
      )}
    </div>
  );
}

// ── Webhooks Tab ──────────────────────────────────────────────────────────────
function WebhooksTab() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newToken, setNewToken] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/settings/webhook-token');
      setInfo(res.data);
    } catch {
      // ignore
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setNewToken('');
    try {
      const res = await apiClient.post('/settings/webhook-token/generate');
      setNewToken(res.data.token);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to generate token');
    } finally { setGenerating(false); }
  };

  const deactivate = async () => {
    if (!window.confirm('Deactivate the webhook token? Existing integrations will stop working.')) return;
    try {
      await apiClient.delete('/settings/webhook-token');
      setNewToken('');
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <LoadingSpinner />;

  const endpoints = info?.endpoints || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={styles.group}>
        <h3 style={styles.groupTitle}>Webhook Token</h3>
        <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 1rem 0' }}>
          n8n (or any HTTP client) sends data to the endpoints below using this Bearer token.
          Generate a new token when setting up your integration.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.875rem' }}>
            Status: {info?.active
              ? <strong style={{ color: '#22c55e' }}>Active</strong>
              : <strong style={{ color: '#94a3b8' }}>None</strong>}
            {info?.maskedToken && <span style={{ color: '#94a3b8', marginLeft: '0.5rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>{info.maskedToken}</span>}
          </div>
          <button onClick={generate} disabled={generating} style={{ padding: '0.4rem 0.875rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>
            {generating ? 'Generating…' : info?.active ? 'Regenerate Token' : 'Generate Token'}
          </button>
          {info?.active && (
            <button onClick={deactivate} style={{ padding: '0.4rem 0.875rem', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>
              Deactivate
            </button>
          )}
        </div>

        {newToken && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '0.875rem', marginBottom: '1rem' }}>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', fontWeight: '700', color: '#166534' }}>⚠️ Copy this token now — it will not be shown again.</p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <code style={{ flex: 1, fontSize: '0.8rem', wordBreak: 'break-all', color: '#1e293b', background: '#fff', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #bbf7d0' }}>{newToken}</code>
              <button onClick={() => copy(newToken)} style={{ padding: '0.4rem 0.875rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                {copied ? '✅ Copied' : '📋 Copy'}
              </button>
            </div>
          </div>
        )}

        <h4 style={{ margin: '0.5rem 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '700', color: '#374151' }}>Webhook Endpoints</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
          {endpoints.map(ep => (
            <div key={ep} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <code style={{ flex: 1, fontSize: '0.75rem', color: '#475569', background: '#f8fafc', padding: '0.4rem 0.625rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0', wordBreak: 'break-all' }}>{ep}</code>
            </div>
          ))}
        </div>

        <h4 style={{ margin: '0.5rem 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '700', color: '#374151' }}>Example Payloads</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
          {[
            { label: 'Revenue', body: '{\n  "year": 2026,\n  "month": 2,\n  "amount": 85000\n}' },
            { label: 'Jobs', body: '{\n  "year": 2026,\n  "month": 2,\n  "count": 142\n}' },
            { label: 'KPI', body: '{\n  "year": 2026,\n  "month": 2,\n  "value": 95,\n  "kpi_name": "Customer Satisfaction"\n}' },
          ].map(ex => (
            <div key={ex.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', marginBottom: '0.375rem' }}>POST /webhooks/{ex.label.toLowerCase()}</div>
              <pre style={{ margin: 0, fontSize: '0.7rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{ex.body}</pre>
            </div>
          ))}
        </div>
        <p style={{ margin: '0.875rem 0 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
          Set <code style={{ background: '#f1f5f9', padding: '0 0.25rem', borderRadius: '0.25rem' }}>Authorization: Bearer {'<token>'}</code> header on all requests.
        </p>
      </div>
    </div>
  );
}

// ── Main Settings Page ────────────────────────────────────────────────────────
export default function Settings() {
  const [activeTab, setActiveTab] = useState(0);
  const [creds, setCreds] = useState({});
  const [hasExisting, setHasExisting] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [error, setError] = useState('');
  const [ghlTest, setGhlTest] = useState(null);
  const [stTest, setStTest] = useState(null);
  const [testingGhl, setTestingGhl] = useState(false);
  const [testingSt, setTestingSt] = useState(false);

  useEffect(() => {
    apiClient.get('/settings/credentials')
      .then(res => {
        const raw = res.data.credentials || {};
        // Secret fields (GHL_API_KEY, ST_CLIENT_SECRET, ST_APP_KEY) come back masked
        // ("abcd****wxyz"), never the real value. Loading that masked string directly into
        // an editable field meant typing without first clearing it appended onto the mask
        // instead of replacing it - the save step then silently skipped anything still
        // containing "****" (to avoid re-saving a placeholder), so newly typed real
        // credentials never actually got saved. Leave secret fields blank on load instead;
        // "hasExisting" just drives the placeholder text.
        const cleaned = {};
        const existing = {};
        for (const [key, val] of Object.entries(raw)) {
          if (SECRET_KEYS.has(key)) {
            existing[key] = !!val;
          } else {
            cleaned[key] = val;
          }
        }
        setCreds(cleaned);
        setHasExisting(existing);
      })
      .catch(() => {});
  }, []);

  const handleChange = (key, val) => setCreds(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true); setSaveMsg(''); setError('');
    try {
      await apiClient.put('/settings/credentials', { credentials: creds });
      setSaveMsg('Credentials saved successfully.');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const testGhl = async () => {
    setTestingGhl(true); setGhlTest(null);
    try {
      const res = await apiClient.post('/settings/test/ghl');
      setGhlTest({ ok: true, message: res.data.message || 'Connection successful' });
    } catch (e) {
      const d = e.response?.data || {};
      setGhlTest({
        ok: false,
        message: d.error || 'Connection failed',
        detail: [
          d.status ? `HTTP ${d.status}` : null,
          d.detail ? JSON.stringify(d.detail) : null,
          d.credsUsed ? `Using: apiKey=${d.credsUsed.hasApiKey ? `${d.credsUsed.apiKeyLength} chars` : 'MISSING'}, locationId=${d.credsUsed.locationId || 'MISSING'}` : null,
        ].filter(Boolean).join(' — '),
      });
    } finally { setTestingGhl(false); }
  };

  const testSt = async () => {
    setTestingSt(true); setStTest(null);
    try {
      const res = await apiClient.post('/settings/test/servicetitan');
      setStTest({ ok: true, message: res.data.message || 'Connection successful' });
    } catch (e) {
      const d = e.response?.data || {};
      setStTest({
        ok: false,
        message: d.error || 'Connection failed',
        detail: [
          d.status ? `HTTP ${d.status}` : null,
          d.detail ? JSON.stringify(d.detail) : null,
          d.credsUsed ? `Using: clientId=${d.credsUsed.hasClientId ? 'set' : 'MISSING'}, secret=${d.credsUsed.hasClientSecret ? 'set' : 'MISSING'}, appKey=${d.credsUsed.hasAppKey ? 'set' : 'MISSING'}, tenant=${d.credsUsed.tenantId || 'MISSING'}, authUrl=${d.credsUsed.authUrl}` : null,
        ].filter(Boolean).join(' — '),
      });
    } finally { setTestingSt(false); }
  };

  return (
    <div>
      <Header title="Settings" />
      <div style={styles.content}>
        <div style={styles.tabBar}>
          {SETTINGS_TABS.map((tab, i) => (
            <button key={tab} onClick={() => setActiveTab(i)} style={{ ...styles.tabBtn, ...(activeTab === i ? styles.tabBtnActive : {}) }}>{tab}</button>
          ))}
        </div>

        {activeTab === 0 && (
          <>
            {error && <ErrorBanner message={error} />}
            {saveMsg && <div style={styles.successBanner}>{saveMsg}</div>}
            <CredentialGroup title="HighLevel (GHL) Credentials" fields={GHL_FIELDS} creds={creds} hasExisting={hasExisting} onChange={handleChange} onTest={testGhl} testing={testingGhl} testResult={ghlTest} />
            <CredentialGroup title="ServiceTitan Credentials" fields={ST_FIELDS} creds={creds} hasExisting={hasExisting} onChange={handleChange} onTest={testSt} testing={testingSt} testResult={stTest} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>
                {saving ? 'Saving…' : 'Save All Credentials'}
              </button>
            </div>
          </>
        )}

        {activeTab === 1 && <TeamAssignmentsTab />}
        {activeTab === 2 && <KpiTargetsTab />}
        {activeTab === 3 && <WebhooksTab />}
      </div>
    </div>
  );
}

const styles = {
  content: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  tabBar: { display: 'flex', gap: '0.25rem', background: '#fff', padding: '0.375rem', borderRadius: '0.625rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '0.25rem' },
  tabBtn: { padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: '#64748b' },
  tabBtnActive: { background: '#6366f1', color: '#fff' },
  group: { background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  groupHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' },
  groupTitle: { margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1e293b' },
  testBtn: { padding: '0.4rem 0.875rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#475569' },
  testResult: { padding: '0.5rem 0.875rem', borderRadius: '0.5rem', fontSize: '0.875rem', marginBottom: '1rem' },
  fieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.875rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
  input: { padding: '0.5rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none' },
  saveBtn: { padding: '0.625rem 1.5rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600' },
  successBanner: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '0.75rem 1rem', borderRadius: '0.5rem', fontSize: '0.875rem' },
};
